/**
 * Returns the expected schema for the create session response object.
 *
 */
export const createSessionSchema: Record<string, unknown> = {
  $schema: 'http://json-schema.org/schema#',
  type: 'object',
  additionalProperties: true,
  properties: {
    sessionContext: {
      $ref: '#/definitions/SessionContext'
    },
    nextService: {
      $ref: '#/definitions/NextService'
    },
    event: {
      description: 'Custom event to be triggered if 404 is returned',
      type: 'string',
      example: 'NeedsPortalOnboarding'
    }
  },
  definitions: {
    OnboardingContext: {
      type: 'object',
      required: ['entry'],
      properties: {
        experience: {
          description: 'Experience type',
          type: 'string',
          enum: [
            'smb',
            'pc',
            'ecp',
            'DeviceSelectedForOobe',
            'DeviceSelectedToAddToCarousel',
            'DeviceSelectedForPostOobePrinterClaim'
          ]
        },
        entry: {
          description: 'How the user entered into this experience',
          type: 'string',
          enum: [
            'portalOobe',
            'earlyOobe',
            'appOobe',
            'hpxOobe',
            'portalEnroll',
            'portalScanSetup',
            'jshellTest'
          ]
        },
        bizModelHint: {
          description:
            'bizModel (E2E, Flex) inferred, for example, through a vanity URL',
          type: 'string',
          example: 'E2E'
        },
        entryUrl: {
          description:
            'Full URL (including path and query string) that initiated the onboarding process, if available',
          type: 'string',
          example:
            'https://admin.hpsmart.com/activate?earlyOobe=easystartwin&printerUuid=abc'
        }
      }
    },
    SessionCreationAppAttributes: {
      type: 'object',
      required: ['clientId', 'osType', 'osVersion', 'container'],
      properties: {
        clientId: {
          description:
            'clientId for this app, as registered with Onboarding Director',
          type: 'string',
          example: 'fdaa06d9-1d5d-4604-91ae-ff886879d36d'
        },
        appVersion: {
          description:
            'Version of the setup app that is hosting the Onboarding Agent, if applicable',
          type: 'string',
          example: 'HP Smart Windows 129.0.3914.0'
        },
        osType: {
          description: 'Operating System',
          type: 'string',
          enum: ['windows', 'macos', 'android', 'ios']
        },
        osVersion: {
          description: 'Operating System version',
          type: 'string',
          example: '10.0.17763.1'
        },
        countryRegionIso: {
          description: 'country and region codes as per ISO 3166-1 alpha-2',
          type: 'string',
          example: 'US'
        },
        locale: {
          description:
            'The combination of user’s language and geographic region, represented as {language-code}_{country-code}, per ISO 3166 and ISO 639, respectively.  Examples are en_GB, zh-Hans_HK, zh_HK.',
          type: 'string',
          example: 'en_GB'
        },
        container: {
          description: 'Type of container',
          type: 'string',
          enum: ['browser', 'jarvisApp', 'nonJarvisApp']
        },
        appInstanceId: {
          description: 'Application instance ID',
          type: 'string',
          example: '12345678-1234-1234-1234-123456789012'
        }
      }
    },
    SessionContextAppAttributes: {
      type: 'object',
      properties: {
        appVersion: {
          description:
            'Version of the setup app that is hosting the Onboarding Agent, if applicable',
          type: 'string',
          example: 'HP Smart Windows 129.0.3914.0'
        },
        osType: {
          description: 'Operating System',
          type: 'string',
          enum: ['windows', 'macos', 'android', 'ios']
        },
        osVersion: {
          description: 'Operating System version',
          type: 'string',
          example: '10.0.17763.1'
        },
        countryRegionIso: {
          description: 'country and region codes as per ISO 3166-1 alpha-2',
          type: 'string',
          example: 'US'
        },
        locale: {
          description:
            'The combination of user’s language and geographic region, represented as {language-code}_{country-code}, per ISO 3166 and ISO 639, respectively.  Examples are en_GB, zh-Hans_HK, zh_HK.',
          type: 'string',
          example: 'en_GB'
        },
        container: {
          description: 'Type of container',
          type: 'string',
          enum: ['browser', 'jarvisApp', 'nonJarvisApp']
        },
        exitOobe: {
          description: 'Inputs required by the ExitOOBE Onboarding Stage',
          type: 'object',
          properties: {
            firstPrintPdfUrl: {
              description: 'URL to PDF used in the First Print activity',
              type: 'string'
            },
            activities: {
              description:
                'List of activities to execute during the ExitOOBE Onboarding Stage',
              type: 'array',
              items: {
                $ref: '#/definitions/ExitOobeActivity'
              }
            }
          }
        }
      }
    },
    ExitOobeActivity: {
      type: 'object',
      required: ['name', 'enabled'],
      properties: {
        name: {
          description: 'Name of ExitOOBE activity',
          type: 'string',
          enum: ['firstPrint', 'printFromOtherDevices']
        },
        enabled: {
          description: 'Whether or not to execute the activity',
          type: 'string',
          enum: [true, false]
        },
        suggestedOrder: {
          description:
            'The suggested order in which to execute this activity, relative to other activities within the ExitOOBE Onboarding Stage. Client can ignore and change the order if needed. Omitted if enabled is \false.',
          type: 'integer'
        }
      }
    },
    SessionCreationDeviceAttributes: {
      type: 'object',
      properties: {
        connectivityToApp: {
          description:
            'Connectity between device and app.  Required if onboardingContext.entry is appOobe.',
          type: 'string',
          enum: ['network', 'usb', 'wifidirect', 'notconnected'],
          example: 'network'
        },
        deviceResources: {
          description:
            'LEDM/CDM resources retrieved from the device.  Required if onboardingContext.entry is appOobe.',
          type: 'array',
          items: {
            $ref: '#/definitions/DeviceResource'
          }
        }
      }
    },
    SessionContextDeviceAttributes: {
      type: 'object',
      properties: {
        uuid: {
          description:
            'device_uuid returned in the response from the DAG User Code Validation API',
          type: 'string'
        },
        productNumber: {
          description:
            'product_number returned in the response from the DAG User Code Validation API',
          type: 'string'
        },
        serialNumber: {
          description:
            'serial_number returned in the response from the DAG User Code Validation API',
          type: 'string'
        },
        modelName: {
          description:
            'device_model returned in the response from the DAG User Code Validation API',
          type: 'string'
        },
        bizModel: {
          description:
            'biz_model returned in the response from the DAG User Code Validation API',
          type: 'string'
        },
        cloudId: {
          description:
            'cloudId returned in the response from the UCDE Register Device API',
          type: 'string'
        },
        deviceLocation: {
          description:
            'deviceLocation provided in the request to the Device Shadow Device Configuration API',
          type: 'string'
        },
        deviceName: {
          description:
            'deviceName provided in the request to the Device Shadow Device Configuration API',
          type: 'string'
        },
        highestResImgUrl: {
          description:
            'URL to the highest resolution image as derived by the onbp-jwv-printer-activation stage from the images array',
          type: 'string'
        },
        pls: {
          description:
            'subset of the data provided in the response from the Product Lookup Service API (for example, images or deviceCapabilities) that may be used by other Onboarding Stages.',
          type: 'object'
        }
      }
    },
    DeviceResource: {
      type: 'object',
      required: ['serviceMetadata'],
      properties: {
        serviceMetadata: {
          description: 'Metadata of the device information',
          type: 'object',
          required: ['fwProtocol', 'serviceGun'],
          properties: {
            fwProtocol: {
              description: 'FW protocol',
              type: 'string',
              enum: ['ledm', 'cdm'],
              example: 'cdm'
            },
            serviceGun: {
              description:
                "Globally Unique Name for the device resource. \nIf serviceMetadata.fwProtocol is cdm', use the CDM serviceGun (or servicesDiscovery for the Services Discovery service). \nIf serviceMetadata.fwProtocol is ledm', use the LEDM relative path.",
              type: 'string',
              example: 'com.hp.cdm.service.system.version.1'
            },
            rel: {
              description: 'CDM rel, if available',
              type: 'string',
              example: 'identity'
            },
            href: {
              description: 'CDM href, if available',
              type: 'string',
              example: '/cdm/system/v1/identity'
            }
          }
        },
        ledmResource: {
          description:
            'String value of the base64-encoding of the corresponding LEDM XML tree. Required if serviceMetadata.fwProtocol is ledm.',
          type: 'string'
        },
        cdmResource: {
          description:
            'JSON object from the corresponding CDM resource. Required if serviceMetadata.fwProtocol is cdm',
          type: 'object'
        }
      }
    },
    User: {
      type: 'object',
      properties: {
        initialAuthenticationState: {
          description:
            'Initial authentication state of the user, based on the bearer token passed in the Create request',
          type: 'string',
          enum: ['notAuthenticated', 'authenticated']
        },
        currentAuthenticationState: {
          description:
            'Current authentication state of the user, based on the bearer token passed in the most recent Update request',
          type: 'string',
          enum: ['notAuthenticated', 'authenticated']
        },
        consents: {
          description: 'array of JSON objects representing the users consents',
          type: 'array',
          items: {
            type: 'object',
            properties: {
              purposeId: {
                description: 'purposeId associated with the consent',
                type: 'string'
              },
              state: {
                description: 'users opt-in status for the given purposeId',
                type: 'string'
              }
            }
          }
        }
      }
    },
    Tenant: {
      type: 'object',
      properties: {
        resourceId: {
          description: 'Account(Tenant) Resource ID',
          type: 'string'
        },
        accountName: {
          description: 'The name of Account(Tenant)',
          type: 'string'
        },
        type: {
          description: 'The type of Account(Tenant)',
          type: 'string'
        }
      }
    },
    DerivedAttributes: {
      type: 'object',
      properties: {
        pls: {
          description:
            'Attributes derived by OD calling the Stratus PLS API, when product number is available',
          type: 'object',
          properties: {
            onboardingCapabilities: {
              description:
                'onboardingCapabilities returned in the response from the Stratus PLS API, if available',
              type: 'object'
            }
          }
        }
      }
    },
    SessionCreation: {
      type: 'object',
      required: ['onboardingContext', 'app'],
      properties: {
        onboardingContext: {
          $ref: '#/definitions/OnboardingContext'
        },
        app: {
          $ref: '#/definitions/SessionCreationAppAttributes'
        },
        device: {
          $ref: '#/definitions/SessionCreationDeviceAttributes'
        }
      }
    },
    SessionContext: {
      type: 'object',
      required: ['sessionId', 'app', 'onboardingContext'],
      properties: {
        sessionId: {
          description: 'Onboarding session ID',
          type: 'string'
        },
        currentServiceId: {
          description:
            'Service Id of the Onboarding Stage currently being executed',
          type: 'string',
          example: '<currentServiceId>'
        },
        xCorrelationId: {
          description:
            'Recommended value for the X-Correlation-ID header to be used by each Onboarding Stage when making external API calls, if the API supports the X-Correlation-ID header.  It is the responsibility of each Onboarding Stage to make sure that the external API call supports the X-Correlation-ID header prior to using this value.',
          type: 'string',
          example: '[<OD sessionId>]-[<currentServiceId>]'
        },
        onboardingContext: {
          $ref: '#/definitions/OnboardingContext'
        },
        app: {
          $ref: '#/definitions/SessionContextAppAttributes'
        },
        device: {
          $ref: '#/definitions/SessionContextDeviceAttributes'
        },
        user: {
          $ref: '#/definitions/User'
        },
        tenant: {
          $ref: '#/definitions/Tenant'
        },
        derivedAttributes: {
          $ref: '#/definitions/DerivedAttributes'
        },
        stepperState: {
          description: 'Array of step data used to render the Stepper UI',
          type: 'array',
          items: {
            type: 'object',
            properties: {
              stepId: {
                description: 'Used by the UI to map to a localized label',
                type: 'string',
                enum: [
                  'onbp-step-assign-ownership',
                  'onbp-step-enter-pairing-code',
                  'onbp-step-instant-ink',
                  'onbp-step-configure-and-test'
                ]
              },
              status: {
                description: 'Status of the step',
                type: 'string',
                enum: ['complete', 'current', 'incomplete', 'disabled']
              },
              order: {
                description: 'Placement of the item on the step sequence',
                type: 'integer'
              }
            }
          }
        }
      }
    },
    NextService: {
      type: 'object',
      properties: {
        serviceId: {
          description: 'Service Id for the Onboarding Agent to execute',
          type: 'string',
          example: '<currentServiceId>'
        },
        resultUrl: {
          description:
            'URL to UPDATE SESSION API, used to report the results of Service <currentServiceId> and to receive the next service to execute',
          type: 'string',
          example: 'https://.../path/to/update/session'
        },
        exitUrl: {
          description:
            'URL or deep-link to which Onboarding Agent should redirect when the onboarding process finishes',
          type: 'string',
          example: 'https://.../path/to/redirect/after/onboarding/finishes'
        }
      }
    },
    OnboardingPlan: {
      type: 'object',
      properties: {
        sessionContext: {
          $ref: '#/definitions/SessionContext'
        },
        nextService: {
          $ref: '#/definitions/NextService'
        },
        event: {
          description:
            'App event to publish - see https://pages.github.azc.ext.hp.com/jarvis/smartapps-docs/swagger/jweb/#/Onboarding%20Events',
          type: 'string'
        }
      }
    },
    Result: {
      description: 'Completion result',
      type: 'string',
      enum: ['success', 'failure', 'declined', 'ineligible'],
      example: 'failure'
    },
    ServiceResult: {
      type: 'object',
      required: ['result'],
      properties: {
        result: {
          $ref: '#/definitions/Result'
        },
        output: {
          description:
            'Output result of the current Service, e.g., any device or user updates that Onboarding Director should store in the current session.  Can be empty.',
          type: 'object',
          example: {
            user: {
              selectedBizModel: 'E2E'
            },
            device: {
              cloudId: 'xyz'
            }
          }
        },
        errorInfo: {
          required: ['errorCode', 'errorDescription'],
          description:
            'Detailed error information. Strongly recommended when result is \failure.',
          type: 'object',
          properties: {
            errorCode: {
              description:
                "Error code in the format \\<currentServiceId\\>.\\<serviceErrorCode\\>', where serviceErrorCode is generated by the Onboarding Stage.",
              type: 'string',
              example: '<currentServiceId>.<serviceErrorCode>'
            },
            errorDescription: {
              description: 'Error description',
              type: 'string'
            }
          }
        },
        xCorrelationId: {
          description:
            'Actual X-Correlation-ID header value used by the Onboarding Stage with any external API calls it made during its execution.',
          type: 'string',
          example: '[<OD sessionId>]-[<currentServiceId>]-[<owsSessionId>]'
        }
      }
    },
    OnbpJwvAccountTypeOutput: {
      type: 'object',
      properties: {
        tenant: {
          description: 'output from the onbp-jwv-account-type Onboarding Stage',
          type: 'object',
          properties: {
            type: {
              description:
                'type provided in the request to the UCDE Create Account API',
              type: 'string'
            }
          }
        }
      }
    },
    OnbpJwvOrganizationOnboardingOutput: {
      type: 'object',
      properties: {
        tenant: {
          description:
            'output from the onbp-jwv-organization-onboarding Onboarding Stage',
          type: 'object',
          properties: {
            resourceId: {
              description:
                'resourceId returned in the response from the UCDE Create Account API',
              type: 'string'
            },
            accountName: {
              description:
                'accountName provided in the request to the UCDE Create Account API',
              type: 'string'
            }
          }
        }
      }
    },
    Hpc3JwvSmbPrinterDataConsentsOutput: {
      type: 'object',
      properties: {
        user: {
          description:
            'output from the hpc3-jwv-smb-printer-data-consents Onboarding Stage',
          type: 'object',
          properties: {
            consents: {
              description:
                'array of JSON objects representing the users consents',
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  purposeId: {
                    description: 'purposeId associated with the consent',
                    type: 'string',
                    example: 'bizops.device.basicfunct.-.printer-v1.print.npf'
                  },
                  state: {
                    description: 'users opt-in status for the given purposeId',
                    type: 'string',
                    example: 'OptedIn'
                  }
                }
              }
            }
          }
        }
      }
    },
    OnbpJwvPrinterCodeCollectionOutput: {
      type: 'object',
      properties: {
        device: {
          description:
            'output from the onbp-jwv-printer-code-collection Onboarding Stage',
          type: 'object',
          properties: {
            uuid: {
              description:
                'device_uuid returned in the response from the DAG User Code Validation API',
              type: 'string'
            },
            productNumber: {
              description:
                'product_number returned in the response from the DAG User Code Validation API',
              type: 'string'
            },
            serialNumber: {
              description:
                'serial_number returned in the response from the DAG User Code Validation API',
              type: 'string'
            },
            bizModel: {
              description:
                'biz_model returned in the response from the DAG User Code Validation API',
              type: 'string'
            },
            modelName: {
              description:
                'device_model returned in the response from the DAG User Code Validation API',
              type: 'string'
            },
            highestResImgUrl: {
              description:
                'URL to the highest resolution image as derived by the onbp-jwv-printer-code-collection stage from the images array',
              type: 'string'
            },
            pls: {
              description:
                'subset of the data provided in the response from the Product Lookup Service API (for example, images or deviceCapabilities) that may be used by other Onboarding Stages.',
              type: 'object'
            }
          }
        }
      }
    },
    OnbpJwvPrinterActivationOutput: {
      type: 'object',
      properties: {
        device: {
          description:
            'output from the onbp-jwv-printer-activation Onboarding Stage',
          type: 'object',
          properties: {
            cloudId: {
              description:
                'cloudId returned in the response from the UCDE Register Device API',
              type: 'string'
            },
            deviceLocation: {
              description:
                'deviceLocation provided in the request to the Device Shadow Device Configuration API',
              type: 'string'
            },
            deviceName: {
              description:
                'deviceName provided in the request to the Device Shadow Device Configuration API',
              type: 'string'
            }
          }
        }
      }
    },
    OwscNtvYetiActivationOutput: {
      type: 'object',
      properties: {
        owscFeatures: {
          description: 'features',
          type: 'array',
          items: {
            description:
              'output from the owsc-ntv-yeti-activation Onboarding Stage',
            type: 'object',
            properties: {
              name: {
                description: 'Name of the OWS feature',
                type: 'string'
              },
              result: {
                $ref: '#/definitions/Result'
              }
            }
          }
        }
      }
    }
  }
}
